import { useEffect, useMemo, useRef } from 'react'
import clsx from 'clsx'
import Debug from 'debug'
import { observer } from 'mobx-react-lite'

import type { GetLinksResponse_Links } from '@jotta/grpc-connect-openapi/wopi'
import type { PathItemObject } from '@jotta/types/Files'
import { getBrandZIndex } from '@jotta/ui/zIndex'

import type { WopiPostMessage, WopiPostMessageType } from './WopiViewContainer'

import styles from './WopiView.module.scss'
const debug = Debug('jotta:files:wopiview')

const wopiWindowZIndex = getBrandZIndex('wopiWindow')

/**
 * View initializing the Office Online iframe view.
 * See a sample page from Microsoft here:
 * https://github.com/Microsoft/Office-Online-Test-Tools-and-Documentation/blob/master/samples/SampleHostPage.html
 * and documentation on how to build a compliant page:
 * https://wopi.readthedocs.io/en/latest/hostpage.html#host-page
 *
 * TODO:
 * Implement ClosePostMessage listening for Close via iframe.
 * https://wopi.readthedocs.io/en/latest/scenarios/customization.html#term-closepostmessage
 * https://wopi.readthedocs.io/en/latest/scenarios/postmessage.html#UI_Close
 *
 * Implement RenamePostMessage
 * https://wopi.readthedocs.io/en/latest/scenarios/postmessage.html#File_Rename
 *
 * Implement UI_Sharing
 * https://wopi.readthedocs.io/en/latest/scenarios/postmessage.html#UI_Sharing
 *
 * @param props.wopiLinks Data to setup view
 */
export const WopiView = observer(function WopiView({
  item,
  onWopiEvent,
  wopiLinks,
  showWopiEditView = false,
}: {
  item: PathItemObject
  onWopiEvent: (e: WopiPostMessageType, data?: string) => void
  wopiLinks: GetLinksResponse_Links
  showWopiEditView?: boolean
}) {
  const formRef = useRef<HTMLFormElement | null>(null)
  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  const hasSentHostReady = useRef(false)
  const fileExtension = getFileExtension(item.path)

  useEffect(() => {
    const el = formRef.current
    if (el) {
      debug('Submit', wopiLinks.actionUrl)
      el.submit()
    } else {
      debug('Form not found')
    }
  }, [wopiLinks.actionUrl])

  useEffect(() => {
    const listener = (evt: MessageEvent) => {
      debug('WopiView: got event data', evt)

      if (!isValidOrigin(evt.origin)) {
        return
      }

      // Send Host_PostmessageReady on first valid message from Office
      // See description here: https://learn.microsoft.com/en-us/microsoft-365/cloud-storage-partner-program/online/scenarios/postmessage#host_postmessageready
      if (!hasSentHostReady.current && iframeRef.current?.contentWindow) {
        hasSentHostReady.current = true
        const readyMsg = {
          MessageId: 'Host_PostmessageReady',
          SendTime: Date.now(),
          Values: {},
        }
        debug('WopiView: sending Host_PostmessageReady', readyMsg)
        iframeRef.current.contentWindow.postMessage(
          JSON.stringify(readyMsg),
          evt.origin,
        )
      }

      const msg =
        typeof evt.data === 'string'
          ? (JSON.parse(evt.data) as WopiPostMessage)
          : (evt.data as WopiPostMessage)
      debug('WopiView: got event ', evt, msg)
      if (!msg.MessageId) {
        // ignore messages without message id
        return
      }
      switch (msg.MessageId) {
        case 'App_LoadingStatus':
          // Office frame has finished loading and is ready for PostMessage communication
          debug('WopiView: received App_LoadingStatus, PostMessage ready')
          return
        case 'File_Rename': {
          const newName = msg.Values.NewName
          if (!newName) {
            debug(
              'Error: office event message is missing the new name! Dropping event',
              msg,
            )
            return
          }
          // For some reason Office Online does not send the new file name with extension in the File_Rename action,
          // so we have to guess it is the same as before.
          // Raw data from event:
          // "{\"MessageId\":\"File_Rename\",\"Values\":{\"NewName\":\"Untitled.v5.final\",\"wdUserSession\":\"e21ea68c-3077-4865-878f-23559c4349f8\"},\"SendTime\":1770724284649,\"CorrelationId\":\"6a6acad0-6bf0-4067-9205-bcea50ccfa41\"}"
          const newFileName = `${newName}.${fileExtension}`
          if (onWopiEvent) {
            onWopiEvent(msg.MessageId, newFileName)
          }
          return
        }
        default: {
          if (onWopiEvent) {
            onWopiEvent(msg.MessageId)
          }
          return
        }
      }
    }
    window.addEventListener('message', listener, false)

    return () => {
      window.removeEventListener('message', listener, false)
    }
  })

  useEffect(() => {
    const { favIconUrl } = wopiLinks
    if (!favIconUrl || favIconUrl === '') {
      return
    }
    const favIcon = document.querySelector(
      'link[rel="icon"][href$="favicon.ico"]',
    )
    const previousFavIconUrl = favIcon?.getAttribute('href')
    if (favIcon && previousFavIconUrl) {
      favIcon.setAttribute('href', favIconUrl)
      return () => {
        favIcon.setAttribute('href', previousFavIconUrl)
      }
    }
  }, [wopiLinks])

  const key = useMemo(() => new Date().getTime(), [])

  // const editSandBoxValues =
  //   wopiLinks && wopiLinks.actionType !== WopiActionType.EMBEDVIEW
  //     ? 'allow-scripts allow-same-origin allow-forms allow-popups allow-top-navigation allow-popups-to-escape-sandbox'
  //     : undefined
  return (
    <div
      id="embedded-wopi-root"
      className={clsx(styles.container, {
        [styles.editview]: showWopiEditView,
      })}
      sx={{
        zIndex: wopiWindowZIndex,
      }}
    >
      <form
        ref={formRef}
        action={wopiLinks.actionUrl}
        method="POST"
        id="office_form"
        name="office_form"
        target="office_frame"
        className={styles.form}
      >
        <input
          name="access_token"
          value={wopiLinks.accessToken}
          type="hidden"
        />
        <input
          name="access_token_ttl"
          value={wopiLinks.accessTokenTtl.toString()}
          type="hidden"
        />
      </form>
      <iframe
        ref={iframeRef}
        key={key}
        id="office_frame"
        name="office_frame"
        title="Office Online"
        // The sandbox attribute is needed to allow automatic redirection to the O365 sign-in page in the business user flow.
        // oxlint flags scripts+same-origin together; both are required for Office Online.
        // oxlint-disable-next-line react/iframe-missing-sandbox
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-top-navigation allow-popups-to-escape-sandbox allow-downloads"
        // This attribute allows true fullscreen mode in slideshow view
        // when using PowerPoint's 'view' action.
        allowFullScreen
      />
    </div>
  )
})

// https://excel.officeapps.live.com
// https://word.officeapps.live.com
function isValidOrigin(origin: string): boolean {
  return (
    /^https:\/\/[\w-]+\.officeapps\.live\.com$/.test(origin) ||
    /^https:\/\/[\w-]+\.officeapps-df\.live\.com$/.test(origin) ||
    /^https:\/\/[\w-]+\.jottacloud.com$/.test(origin)
  )
}

// input: test.docs.final.v4.docx
// output: docx
export function getFileExtension(filename: string) {
  const idx = filename.lastIndexOf('.')
  if (idx === -1) {
    return ''
  }
  // idx would give: .docx
  // idx + 1 gives: docx
  return filename.substring(idx + 1)
}
